import React, { useEffect, useRef } from "react";
import {
  View,
  Animated,
  Easing,
  StyleSheet,
  Dimensions,
  Platform,
} from "react-native";

interface ConfettiProps {
  trigger: boolean;
  count?: number;
  duration?: number;
}

const { width: SCREEN_WIDTH, height: SCREEN_HEIGHT } = Dimensions.get("window");
const CONFETTI_COLORS = [
  "#FFD700",
  "#FF69B4",
  "#00CED1",
  "#FF6347",
  "#98FB98",
  "#DDA0DD",
];

const Confetti: React.FC<ConfettiProps> = ({
  trigger,
  count = 50,
  duration = 5000,
}) => {
  const confettiRefs = useRef<Animated.Value[]>([]);
  const confettiConfig = useRef<
    Array<{
      color: string;
      startX: number;
      startY: number;
      endX: number;
      endY: number;
      rotation: number;
      scale: number;
    }>
  >([]);

  useEffect(() => {
    if (trigger) {
      // Initialize confetti pieces
      confettiRefs.current = Array(count)
        .fill(0)
        .map(() => new Animated.Value(0));
      confettiConfig.current = Array(count)
        .fill(0)
        .map(() => ({
          color:
            CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
          startX: Math.random() * SCREEN_WIDTH,
          startY: -20,
          endX: Math.random() * SCREEN_WIDTH,
          endY: SCREEN_HEIGHT + 20,
          rotation: Math.random() * 360,
          scale: 0.5 + Math.random() * 0.5,
        }));

      // Animate each piece
      const animations = confettiRefs.current.map((anim) =>
        Animated.timing(anim, {
          toValue: 1,
          duration,
          easing: Easing.linear,
          useNativeDriver: Platform.OS !== "web",
        }),
      );

      Animated.stagger(50, animations).start();
    }
  }, [trigger, count, duration]);

  if (!trigger) return null;

  return (
    <View
      style={
        Platform.OS === "web"
          ? [StyleSheet.absoluteFill, { pointerEvents: "none" }]
          : StyleSheet.absoluteFill
      }
      pointerEvents={Platform.OS !== "web" ? "none" : undefined}
      data-oid="1.afkyd"
    >
      {confettiRefs.current.map((anim, index) => {
        const config = confettiConfig.current[index];
        return (
          <Animated.View
            key={index}
            style={[
              styles.confetti,
              {
                backgroundColor: config.color,
                transform: [
                  {
                    translateX: anim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [config.startX, config.endX],
                    }),
                  },
                  {
                    translateY: anim.interpolate({
                      inputRange: [0, 1],
                      outputRange: [config.startY, config.endY],
                    }),
                  },
                  {
                    rotate: anim.interpolate({
                      inputRange: [0, 1],
                      outputRange: ["0deg", `${config.rotation}deg`],
                    }),
                  },
                  { scale: config.scale },
                ],

                opacity: anim.interpolate({
                  inputRange: [0, 0.7, 1],
                  outputRange: [1, 1, 0],
                }),
              },
            ]}
            data-oid="31u2vh7"
          />
        );
      })}
    </View>
  );
};

const styles = StyleSheet.create({
  confetti: {
    position: "absolute",
    width: 10,
    height: 10,
    borderRadius: 5,
  },
});

export default Confetti;
